/* page.tsx generated by @compiled/babel-plugin v0.38.1 */
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import "./page.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
import { forwardRef, useEffect, useState } from 'react';
import { useStaticCallback } from '@atlaskit/media-common';
import { fg } from '@atlaskit/platform-feature-flags';
import Spinner from '@atlaskit/spinner';
import { Annotations } from './annotations';
import { DocumentLinks } from './documentLinks';
import { getDocumentRoot } from './utils/getDocumentRoot';
import { getImageZoom } from './utils/useCachedGetImage';
import { useIntersectionObserver } from './utils/useIntersectionObserver';
var Span = function Span(_ref) {
  var span = _ref.span,
    font = _ref.font;
  var width = span.l;
  var height = span.h;
  if (width <= 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement("tspan", {
    lengthAdjust: "spacingAndGlyphs",
    x: span.x,
    y: "-".concat(span.y),
    height: height,
    textLength: width,
    style: _defineProperty(_defineProperty({}, 'fontFamily', "".concat(font.name)), 'fontSize', "".concat(font.size === 1 ? height : font.size, "px"))
  }, span.text);
};
var textStyles = null;
var Line = function Line() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      spans: [],
      rotation: 0,
      fonts: []
    },
    spans = _ref3.spans,
    rotation = _ref3.rotation,
    fonts = _ref3.fonts,
    dataTestId = _ref3.dataTestId;
  return /*#__PURE__*/React.createElement("text", {
    opacity: "0.5",
    // eslint-disable-next-line react/no-unknown-property
    "transform-origin": "".concat(spans[0].x, " -").concat(spans[0].y),
    style: {
      transform: "rotate(".concat(rotation, "rad)")
    },
    "data-testid": dataTestId,
    className: ax(["_o572qvpr"])
  }, spans.map(function (span, i) {
    return /*#__PURE__*/React.createElement(Span, {
      span: span,
      font: fonts[span.fi],
      key: i
    });
  }));
};
var pageSvgStyles = null;
var pageImageStyles = null;
var pixelatedImageRendering = null;
var pageSpinnerStyles = null;
var pageWrapperStyles = null;
var a4Dimensions = {
  height: 595,
  width: 842
};
var PageView = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var dimensions = _ref4.dimensions,
    imageSrc = _ref4.imageSrc,
    content = _ref4.content,
    fonts = _ref4.fonts,
    pageIndex = _ref4.pageIndex,
    zoom = _ref4.zoom,
    onImageLoad = _ref4.onImageLoad;
  var style = {};
  if (dimensions) {
    // contents endpoint has loaded so dimensions are available
    style.width = "calc(var(--document-viewer-zoom) * ".concat(dimensions.width, "px)");
    style.height = "calc(var(--document-viewer-zoom) * ".concat(dimensions.height, "px)");
  } else if (imageSrc) {
    // contents endpoint has not loaded dimensions but image is loaded so we can use the image dimensions
    style.width = 'initial';
    style.height = 'initial';
  } else {
    // contents endpoint has not loaded dimensions and image is not loaded so we can use the a4 dimensions
    style.width = "calc(var(--document-viewer-zoom) * ".concat(a4Dimensions.width, "px)");
    style.height = "calc(var(--document-viewer-zoom) * ".concat(a4Dimensions.height, "px)");
  }
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: style,
    "data-testid": "page-".concat(pageIndex),
    id: "page-".concat(pageIndex + 1),
    className: ax(["_kqswh2mm _bfhku67f _18u01wug _2hwx1wug"])
  }, !imageSrc && /*#__PURE__*/React.createElement("div", {
    className: ax(["_4t3i1osq _1bsb1osq _1e0c1txw _1bah1h6o _4cvr1h6o"])
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: "large"
  })), imageSrc && /*#__PURE__*/React.createElement("img", {
    style: style,
    "data-testid": "page-".concat(pageIndex, "-image"),
    "data-zoom": zoom,
    src: imageSrc,
    alt: "",
    onLoad: onImageLoad,
    className: ax(["_kqswstnw _154iidpf _1ltvidpf _uiztglyw", !fg('media-document-viewer-clear-render') && "_1kemd8h4"])
  }), content && /*#__PURE__*/React.createElement("svg", {
    "data-testid": "page-".concat(pageIndex, "-text-layer"),
    style: style,
    viewBox: "0 -".concat(content.height, " ").concat(content.width, " ").concat(content.height),
    className: ax(["_kqswstnw _154iidpf _1ltvidpf _uizt1kdv _80om1kdv _lswu1j28"])
  }, content.lines.map(function (line, i) {
    return /*#__PURE__*/React.createElement(Line, {
      dataTestId: "page-".concat(pageIndex, "-line-").concat(i),
      spans: line.spans,
      rotation: line.r,
      fonts: fonts,
      key: i
    });
  }), content.annotations && /*#__PURE__*/React.createElement(Annotations, {
    annotations: content.annotations
  }), content.links && /*#__PURE__*/React.createElement(DocumentLinks, {
    links: content.links
  })));
});
export var Page = function Page(_ref5) {
  var _ref6;
  var getPageSrc = _ref5.getPageSrc,
    maxPageImageZoom = _ref5.maxPageImageZoom,
    content = _ref5.content,
    fonts = _ref5.fonts,
    pageIndex = _ref5.pageIndex,
    zoom = _ref5.zoom,
    defaultDimensions = _ref5.defaultDimensions,
    onVisible = _ref5.onVisible,
    onLoad = _ref5.onLoad;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    imageSrc = _useState2[0],
    setImageSrc = _useState2[1];
  var _useIntersectionObser = useIntersectionObserver({
      root: getDocumentRoot(),
      // this will load the page 3 pages before or after the current page
      // ensuring that the page is loaded before the user scrolls to it
      rootMargin: '300%',
      threshold: 0.1
    }, function () {
      onVisible();
      getPageSrc(pageIndex, zoom).then(setImageSrc);
    }),
    observedRef = _useIntersectionObser.observedRef,
    isVisibleRef = _useIntersectionObser.isVisibleRef;
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    dimensions = _useState4[0],
    setDimensions = _useState4[1];
  var onImageLoad = useStaticCallback(function (event) {
    var image = event.currentTarget;
    onLoad === null || onLoad === void 0 || onLoad();
    if (!content) {
      var _zoom = image.dataset.zoom ? Number(image.dataset.zoom) : 1;
      var imageZoom = getImageZoom(_zoom, maxPageImageZoom);
      var contentWidth = image.naturalWidth / imageZoom;
      var contentHeight = image.naturalHeight / imageZoom;
      setDimensions({
        width: contentWidth,
        height: contentHeight
      });
    }
  });
  useEffect(function () {
    if (isVisibleRef.current) {
      getPageSrc(pageIndex, zoom).then(setImageSrc);
    }
  }, [isVisibleRef, pageIndex, zoom, getPageSrc]);
  return /*#__PURE__*/React.createElement(PageView, {
    ref: observedRef,
    dimensions: (_ref6 = content !== null && content !== void 0 ? content : dimensions) !== null && _ref6 !== void 0 ? _ref6 : defaultDimensions,
    imageSrc: imageSrc,
    content: content,
    fonts: fonts,
    pageIndex: pageIndex,
    zoom: zoom,
    onImageLoad: onImageLoad
  });
};